import { useMemo, useState } from "react";
import type { CellClickedEvent, ColDef, GridOptions, RowClickedEvent } from "ag-grid-community";
import { IconMenuHorizontalDots } from "@synthesis/shared-icons";
import { Dropdown, Table, type DropdownScheme, type StateProps } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";

export type { ColDef } from "ag-grid-community";

/** Данные меню строки (три точки): схема пунктов и обработчики (по id пункта). */
export type RowMenuData = {
  scheme: DropdownScheme;
  state: StateProps;
};

/** Ячейка меню строки: три точки открывают Dropdown с действиями строки. */
const AdminRowMenuCell = ({ data, renderRowMenu }: { data?: unknown; renderRowMenu: (row: unknown) => RowMenuData | null }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!data) {
    return null;
  }

  const menu = renderRowMenu(data);

  if (!menu) {
    return null;
  }

  return (
    <Dropdown
      size="sm"
      scheme={menu.scheme}
      state={menu.state}
      onOpenChange={setIsOpen}
      trigger={
        // Не-<button> триггер: Base UI с nativeButton=false сам вешает role/обработчики через render-пропсы.
        <span
          aria-label="Действия"
          className={cn(
            "hover:bg-neutrals-95/80 inline-flex size-8 cursor-pointer items-center justify-center rounded-lg transition-colors",
            isOpen && "bg-neutrals-95/80",
          )}
        >
          <IconMenuHorizontalDots className="text-neutrals-60 size-5" aria-hidden />
        </span>
      }
    />
  );
};

/** Иконка «меню строки» для прямого действия (onRowMenu): клик по ⋮ обрабатывает сетка (onCellClicked). */
const AdminRowMenuIcon = (params: { data?: unknown }) => {
  if (!params.data) {
    return null;
  }

  return (
    <button
      type="button"
      aria-label="Действия"
      className="hover:bg-neutrals-95/80 inline-flex size-8 items-center justify-center rounded-lg transition-colors"
    >
      <IconMenuHorizontalDots className="text-neutrals-60 size-5" aria-hidden />
    </button>
  );
};

/**
 * Таблица ЛК на AG Grid (обёртка над `Table` из @synthesis/shared-ui, как на странице заявок).
 * Строки 48px, колонка действий с меню (…) справа, быстрый поиск через quickFilterText.
 * Меню строки: либо напрямую `onRowMenu` (клик по ⋮), либо dropdown `renderRowMenu` (Изменить/Удалить).
 */
export const AdminGridTable = <T,>({
  tableId,
  columns,
  rows,
  quickFilterText,
  rowHeight = 48,
  className,
  isLoading,
  onRowClicked,
  onRowMenu,
  renderRowMenu,
  masterDetail,
  detailCellRenderer,
  detailRowHeight,
  detailRowAutoHeight,
  isRowMaster,
}: {
  tableId: string;
  columns: ColDef<T>[];
  rows: T[];
  quickFilterText?: string;
  rowHeight?: number;
  className?: string;
  isLoading?: boolean;
  onRowClicked?: (row: T) => void;
  onRowMenu?: (row: T) => void;
  renderRowMenu?: (row: T) => RowMenuData | null;
  /** Master/detail: раскрытие строки с кастомной панелью (detail cell renderer). */
  masterDetail?: boolean;
  detailCellRenderer?: GridOptions<T>["detailCellRenderer"];
  detailRowHeight?: number;
  detailRowAutoHeight?: boolean;
  /** Master/detail: какие строки можно раскрывать (по умолчанию — все). */
  isRowMaster?: GridOptions<T>["isRowMaster"];
}) => {
  const hasMenu = Boolean(onRowMenu) || Boolean(renderRowMenu);

  const dropdownMenuCellRenderer = useMemo<ColDef<T>["cellRenderer"] | undefined>(() => {
    if (!renderRowMenu) {
      return undefined;
    }

    return (params: { data?: unknown }) => (
      <AdminRowMenuCell data={params.data} renderRowMenu={renderRowMenu as (row: unknown) => RowMenuData | null} />
    );
  }, [renderRowMenu]);

  const tableColumns = useMemo(() => {
    if (!hasMenu) {
      return columns;
    }

    const menuColumn: ColDef<T> = {
      colId: "actions",
      headerName: "",
      width: 64,
      maxWidth: 64,
      pinned: "right",
      sortable: false,
      suppressHeaderMenuButton: true,
      cellRenderer: (dropdownMenuCellRenderer ?? AdminRowMenuIcon) as ColDef<T>["cellRenderer"],
    };

    return [...columns, menuColumn];
  }, [columns, hasMenu, dropdownMenuCellRenderer]);

  const handleCellClicked = (event: CellClickedEvent<T>) => {
    if (event.colDef.colId === "actions" && event.data) {
      onRowMenu?.(event.data);
    }
  };

  return (
    <div className={cn("w-full", className)}>
      <Table<T>
        tableId={tableId}
        module="ekptd"
        tableColumns={tableColumns}
        tableData={rows}
        rowHeight={rowHeight}
        hasFilters={false}
        isLoading={isLoading}
        quickFilterText={quickFilterText}
        masterDetail={masterDetail}
        detailCellRenderer={detailCellRenderer}
        detailRowHeight={detailRowHeight}
        detailRowAutoHeight={detailRowAutoHeight}
        isRowMaster={isRowMaster}
        onCellClicked={onRowMenu && !renderRowMenu ? handleCellClicked : undefined}
        onRowClicked={onRowClicked ? (event: RowClickedEvent<T>) => (event.data ? onRowClicked(event.data) : undefined) : undefined}
        className="block h-full min-w-0"
      />
    </div>
  );
};
